import { useRef, useState, useEffect } from "react";
import { Bold, Italic, Heading, List, Link as LinkIcon, Eye, Code2 } from "lucide-react";

/**
 * Minimal contentEditable rich text editor with toolbar.
 * Emits HTML via onChange. ~120 lines, no dependencies.
 */
export default function RichTextEditor({ value, onChange, placeholder, testid }) {
  const editorRef = useRef(null);
  const [showHtml, setShowHtml] = useState(false);

  // Keep the editor's innerHTML in sync if the parent resets `value`
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== (value || "")) {
      editorRef.current.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    emit();
  };

  const emit = () => {
    if (!editorRef.current) return;
    onChange?.(editorRef.current.innerHTML);
  };

  const insertLink = () => {
    const url = window.prompt("Link URL (https://...)");
    if (!url) return;
    exec("createLink", url);
  };

  return (
    <div className="border border-neutral-800 bg-black" data-testid={testid || "rich-editor"}>
      <div className="flex items-center gap-1 p-2 border-b border-neutral-900">
        <ToolBtn onClick={() => exec("bold")} icon={<Bold className="w-3.5 h-3.5" />} title="Bold" testid="rt-bold" />
        <ToolBtn onClick={() => exec("italic")} icon={<Italic className="w-3.5 h-3.5" />} title="Italic" testid="rt-italic" />
        <ToolBtn onClick={() => exec("formatBlock", "<h2>")} icon={<Heading className="w-3.5 h-3.5" />} title="Heading" testid="rt-heading" />
        <ToolBtn onClick={() => exec("insertUnorderedList")} icon={<List className="w-3.5 h-3.5" />} title="List" testid="rt-list" />
        <ToolBtn onClick={insertLink} icon={<LinkIcon className="w-3.5 h-3.5" />} title="Link" testid="rt-link" />
        <div className="flex-1" />
        <ToolBtn
          onClick={() => setShowHtml(false)}
          active={!showHtml}
          icon={<Eye className="w-3.5 h-3.5" />} title="Visual editor" testid="rt-visual"
        />
        <ToolBtn
          onClick={() => setShowHtml(true)}
          active={showHtml}
          icon={<Code2 className="w-3.5 h-3.5" />} title="HTML source" testid="rt-html"
        />
      </div>
      {showHtml ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          data-testid="rt-html-source"
          rows={12}
          className="w-full bg-black text-neutral-300 font-mono text-xs p-3 focus:outline-none resize-y"
          spellCheck={false}
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable
          onInput={emit}
          onBlur={emit}
          data-placeholder={placeholder}
          data-testid="rt-content"
          className="min-h-[220px] max-h-[60vh] overflow-y-auto p-4 text-sm text-white leading-relaxed focus:outline-none rich-text-editor"
          suppressContentEditableWarning
        />
      )}
      <style>{`
        .rich-text-editor:empty:before {
          content: attr(data-placeholder);
          color: #525252;
          pointer-events: none;
        }
        .rich-text-editor h2 { font-size: 1.25rem; font-weight: 900; margin: 0.75rem 0; letter-spacing: -0.01em; }
        .rich-text-editor ul { padding-left: 1.25rem; list-style: disc; margin: 0.5rem 0; }
        .rich-text-editor a { color: #00E5FF; text-decoration: underline; }
        .rich-text-editor p { margin: 0.5rem 0; }
      `}</style>
    </div>
  );
}

function ToolBtn({ onClick, icon, title, active, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`p-2 transition-colors ${
        active ? "bg-neutral-900 text-white" : "text-neutral-400 hover:text-white hover:bg-neutral-900"
      }`}
    >{icon}</button>
  );
}
